import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../auth/AuthProvider'
import { formatDateTime } from '../lib/datetime'
import { effectiveStatus } from '../lib/status'
import { usePageTitle } from '../lib/usePageTitle'

interface Question {
  id: string
  q_no: number
  label: string
  opt_1: string
  opt_x: string
  opt_2: string
}

interface Answer {
  q_no: number
  label: string
  pick: '1' | 'X' | '2'
  pick_text: string
  is_correct: boolean | null
}

interface Entry {
  entry_id: string
  user_id: string
  display_name: string
  rank: number | null
  score: number | null
  tiebreak_guess: number | null
  tiebreak_delta: number | null
  answers: Answer[]
}

interface Tournament {
  id: string
  name: string
  status: string
  lock_time: string
  starts_at: string
  stryktips_enabled: boolean
  stryktips_tiebreak_label: string | null
  stryktips_tiebreak_actual: number | null
}

const OPTIONS: Array<'1' | 'X' | '2'> = ['1', 'X', '2']

export function Stryktips() {
  const { id = '' } = useParams()
  const { session, profile } = useAuth()

  const [tournament, setTournament] = useState<Tournament | null>(null)
  const [questions, setQuestions] = useState<Question[]>([])
  const [board, setBoard] = useState<Entry[]>([])
  const [picks, setPicks] = useState<Record<string, '1' | 'X' | '2'>>({})
  const [tiebreak, setTiebreak] = useState<string>('')
  const [entryId, setEntryId] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  usePageTitle(tournament ? `Stryktips ${tournament.name}` : 'Stryktips')

  const load = useCallback(async () => {
    const [t, q, b] = await Promise.all([
      supabase
        .from('tournaments')
        .select('id, name, status, lock_time, starts_at, stryktips_enabled, stryktips_tiebreak_label, stryktips_tiebreak_actual')
        .eq('id', id)
        .maybeSingle(),
      supabase
        .from('stryktips_questions')
        .select('id, q_no, label, opt_1, opt_x, opt_2')
        .eq('tournament_id', id)
        .order('q_no'),
      supabase
        .from('stryktips_scoreboard')
        .select('entry_id, user_id, display_name, rank, score, tiebreak_guess, tiebreak_delta, answers')
        .eq('tournament_id', id),
    ])

    if (t.error) setError(t.error.message)
    else setTournament(t.data as Tournament)
    if (q.error) setError(q.error.message)
    else setQuestions((q.data as Question[]) ?? [])

    if (!b.error) {
      const rows = (b.data as unknown as Entry[]) ?? []
      rows.sort((a, c) => (a.rank ?? 9999) - (c.rank ?? 9999))
      setBoard(rows)

      const mine = rows.find((r) => r.user_id === session?.user.id)
      if (mine) {
        setEntryId(mine.entry_id)
        setTiebreak(mine.tiebreak_guess === null ? '' : String(mine.tiebreak_guess))
      }
    }

    setLoading(false)
  }, [id, session])

  useEffect(() => {
    void load()
  }, [load])

  // Egna svar hämtas separat: scoreboardvyn ger dem bara som text, och
  // formuläret behöver fråge-id:n.
  useEffect(() => {
    if (!entryId) return
    supabase
      .from('stryktips_picks')
      .select('question_id, pick')
      .eq('entry_id', entryId)
      .then(({ data }) => {
        const next: Record<string, '1' | 'X' | '2'> = {}
        for (const p of (data ?? []) as Array<{ question_id: string; pick: '1' | 'X' | '2' }>) {
          next[p.question_id] = p.pick
        }
        setPicks(next)
      })
  }, [entryId])

  // Tipset stänger med KLOCKAN, samtidigt som laginlämningen — inte med
  // databasens status. RLS avvisar ändå varje spar-försök efter lock_time
  // (picks_open), så ett formulär som ser öppet ut efter låset är bara ett
  // sätt att lova något databasen tänker neka.
  const open = tournament ? effectiveStatus(tournament) === 'open' : false
  const locked = !open
  const answered = Object.keys(picks).length
  const complete = answered === questions.length && questions.length > 0 && tiebreak !== ''

  const myEntry = useMemo(
    () => board.find((b) => b.user_id === session?.user.id),
    [board, session],
  )

  async function save() {
    if (!session) return
    setBusy(true)
    setError(null)

    let eid = entryId
    if (!eid) {
      const { data, error } = await supabase
        .from('stryktips_entries')
        .insert({
          tournament_id: id,
          user_id: session.user.id,
          tiebreak_guess: tiebreak === '' ? null : Number(tiebreak),
        })
        .select('id')
        .single()
      if (error) { setError(error.message); setBusy(false); return }
      eid = data.id as string
      setEntryId(eid)
    } else {
      const { error } = await supabase
        .from('stryktips_entries')
        .update({
          tiebreak_guess: tiebreak === '' ? null : Number(tiebreak),
          submitted_at: new Date().toISOString(),
        })
        .eq('id', eid)
      if (error) { setError(error.message); setBusy(false); return }
    }

    const rows = Object.entries(picks).map(([question_id, pick]) => ({
      entry_id: eid,
      question_id,
      pick,
    }))

    if (rows.length > 0) {
      const { error } = await supabase
        .from('stryktips_picks')
        .upsert(rows, { onConflict: 'entry_id,question_id' })
      if (error) { setError(error.message); setBusy(false); return }
    }

    setBusy(false)
    setSaved(true)
    void load()
  }

  if (loading) return <div className="page"><p className="muted">Hämtar stryktipset…</p></div>
  if (!tournament) return <div className="page"><p className="muted">Turneringen finns inte.</p></div>

  if (!tournament.stryktips_enabled) {
    return (
      <div className="page stack">
        <h1>Stryktips</h1>
        <div className="card">
          <p className="muted">Stryktipset är avstängt för {tournament.name}.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="page stack">
      <div>
        <p className="eyebrow">{tournament.name}</p>
        <h1>Stryktips</h1>
        <p className="muted">
          {open
            ? `Öppet till ${formatDateTime(tournament.lock_time)}`
            : 'Stängt — svaren är låsta'}
          {myEntry?.score !== null && myEntry?.score !== undefined && ` · du har ${myEntry.score} rätt`}
        </p>
      </div>

      {error && <div className="notice notice--error">{error}</div>}

      {questions.length === 0 && (
        <div className="card">
          <p className="muted">
            Inga frågor upplagda än.
            {profile?.role !== 'deltagare' && (
              <> <Link to={`/turnering/${id}/stryktips/facit`}>Lägg upp dem här.</Link></>
            )}
          </p>
        </div>
      )}

      {/* ---- Tipsformuläret ---- */}
      {open && questions.length > 0 && (
        <section className="card stack">
          <h2>Dina tips ({answered}/{questions.length})</h2>

          {questions.map((q) => (
            <div key={q.id} className="tips-question">
              <div className="tips-question__label">
                <span className="tips-question__no">{q.q_no}</span>
                {q.label}
              </div>
              <div className="tips-question__options">
                {OPTIONS.map((o) => {
                  const text = o === '1' ? q.opt_1 : o === 'X' ? q.opt_x : q.opt_2
                  const chosen = picks[q.id] === o
                  return (
                    <button
                      key={o}
                      type="button"
                      className={`tips-option${chosen ? ' tips-option--chosen' : ''}`}
                      onClick={() => {
                        setPicks({ ...picks, [q.id]: o })
                        setSaved(false)
                      }}
                    >
                      <span className="tips-option__key">{o}</span>
                      <span className="tips-option__text">{text}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}

          <div>
            <label htmlFor="tiebreak">
              Utslagsfråga{tournament.stryktips_tiebreak_label ? `: ${tournament.stryktips_tiebreak_label}` : ''}
            </label>
            <input
              id="tiebreak"
              type="number"
              step="any"
              value={tiebreak}
              onChange={(e) => { setTiebreak(e.target.value); setSaved(false) }}
              style={{ maxWidth: 180 }}
            />
            <p className="muted">Närmast rätt vinner vid lika antal rätt.</p>
          </div>

          {saved && <div className="notice notice--ok">Sparat.</div>}

          <div>
            <button className="primary" onClick={() => void save()} disabled={busy || answered === 0}>
              {busy ? 'Sparar…' : complete ? 'Spara tipset' : 'Spara det jag hunnit'}
            </button>
          </div>
        </section>
      )}

      {/* ---- Scoreboarden ---- */}
      {board.length > 0 && (
        <section className="stack">
          <h2>Ställning</h2>
          {locked && tournament.stryktips_tiebreak_actual !== null && (
            <p className="muted">
              Utslagsfrågans facit: {tournament.stryktips_tiebreak_actual}
            </p>
          )}

          <div className="board">
            {board.map((e) => {
              const isOpen = expanded === e.entry_id
              return (
                <div key={e.entry_id} className={`board__team${isOpen ? ' board__team--open' : ''}`}>
                  <button
                    className="board__row"
                    onClick={() => setExpanded(isOpen ? null : e.entry_id)}
                    aria-expanded={isOpen}
                  >
                    <span className="board__pos">{e.rank ?? '–'}</span>
                    <span className="board__name">
                      {e.display_name}
                      {e.user_id === session?.user.id && ' · du'}
                    </span>
                    {e.tiebreak_delta !== null && (
                      <span className="board__penalty">Δ {e.tiebreak_delta}</span>
                    )}
                    <span className="board__score">{e.score ?? '–'}</span>
                    <span className="board__chevron">{isOpen ? '−' : '+'}</span>
                  </button>

                  {isOpen && (
                    <div className="board__detail">
                      {e.answers.map((a) => (
                        <div key={a.q_no} className="tips-answer">
                          <span className="tips-answer__no">{a.q_no}</span>
                          <span className="tips-answer__label">{a.label}</span>
                          <span
                            className={
                              'tips-answer__pick' +
                              (a.is_correct === true ? ' tips-answer__pick--right' : '') +
                              (a.is_correct === false ? ' tips-answer__pick--wrong' : '')
                            }
                          >
                            {a.pick} · {a.pick_text}
                          </span>
                        </div>
                      ))}
                      {e.tiebreak_guess !== null && (
                        <p className="muted" style={{ padding: '8px 14px 0' }}>
                          Utslagsfråga: {e.tiebreak_guess}
                          {e.tiebreak_delta !== null && ` (${e.tiebreak_delta} från rätt)`}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {open && (
            <p className="muted">
              Andras svar visas först när tipset stängt.
            </p>
          )}
        </section>
      )}

      {profile && profile.role !== 'deltagare' && (
        <div>
          <Link to={`/turnering/${id}/stryktips/facit`}>
            <button>Rättarpanelen</button>
          </Link>
        </div>
      )}
    </div>
  )
}
